// card #3196 (AC1) - a signed sheet's signature IMAGE must still paint offline.
// score-api hands back a presigned S3 URL that expires in minutes
// (SIGNATURE_URL_TTL_SECONDS), so a cached URL is a broken image the next time
// the scribe is offline. While online we therefore also keep the image BYTES as
// a data URL beside the record, read back from an <img> through a canvas (the
// no-naked-fetch rule forbids fetch/XHR in FE source; the signatures bucket
// answers CORS for this, see ScoreSignaturesBucket in score-api's template).
// Everything here is best effort: any failure yields null and the caller keeps
// the record without an image, which renders as an empty box, never a broken one.

const LOAD_TIMEOUT_MS = 5000;

export function imageUrlToDataUrl(url, { timeoutMs = LOAD_TIMEOUT_MS } = {}) {
  return new Promise((resolve) => {
    if (!url || typeof Image === "undefined") {
      resolve(null);
      return;
    }
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext("2d").drawImage(img, 0, 0);
        finish(canvas.toDataURL("image/png"));
      } catch {
        finish(null);
      }
    };
    img.onerror = () => finish(null);
    img.src = url;
  });
}

const hashOf = (rec) => rec?.signatureHash ?? null;

// Attach `dataUrl` to the record (and to each composite judge) from a fresh
// score-api answer. `prior` is the previously cached record: signatures are
// content addressed by hash, so an unchanged hash reuses the bytes already held.
export async function withInlinedImages(fresh, prior, toDataUrl = imageUrlToDataUrl) {
  if (!fresh) return fresh;
  const inline = async (rec, priorRec) => {
    if (!rec?.url) return rec;
    const reuse = hashOf(rec) && hashOf(rec) === hashOf(priorRec) ? priorRec?.dataUrl : null;
    const dataUrl = reuse || (await toDataUrl(rec.url));
    return dataUrl ? { ...rec, dataUrl } : rec;
  };
  const out = await inline(fresh, prior);
  if (fresh.composite?.judges) {
    const judges = await Promise.all(
      fresh.composite.judges.map((j) =>
        inline(j, prior?.composite?.judges?.find((p) => p.judgeId === j.judgeId)),
      ),
    );
    return { ...out, composite: { ...fresh.composite, judges } };
  }
  return out;
}

// What a CACHED record paints: the stored bytes, never the stale presigned URL.
// No bytes held -> url null -> SignatureBox renders nothing (no broken image).
export function cachedSignatureView(cached) {
  if (!cached) return cached;
  const view = (rec) => ({ ...rec, url: rec?.dataUrl ?? null });
  const out = view(cached);
  if (cached.composite?.judges) {
    return { ...out, composite: { ...cached.composite, judges: cached.composite.judges.map(view) } };
  }
  return out;
}
